import React from "react";
import { useState, useEffect, useRef, useCallback  } from "react";
import useSWR from "swr";
import { list } from "../api/api";
import { ContactTable } from "../components/ContactTable";
import { Upload } from "../components/Upload";
import ExpandableSection from "@cloudscape-design/components/expandable-section";
import { useHistory } from 'react-router-dom';
import { AlertBanner } from "../components/AlertBanner";
import { DashboardMetrics } from "../components/DashboardMetrics";
import { useDangerAlert } from "../hooks/useAlert";
import { presign } from "../api/api";
import { useUploadProgress } from '../hooks/UploadProgressContext';
import AudioProcessingService from '../util/audioProcessingService';

import {
  Button,
  ContentLayout,
  Link,
  Header,
  BreadcrumbGroup,
  Grid,
  FileUpload,
  FormField,
  Form,
  SpaceBetween,
  Spinner,
  ColumnLayout,
  Container,
  ProgressBar,
  StatusIndicator,  
  Box
} from '@cloudscape-design/components';

import { useTranslation } from 'react-i18next';
import "../locales/i18n";
import { useUserRole } from "../api/role";

const config = window.pcaSettings;

function Home({ setAlert }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageKeys, setPageKeys] = useState([]);
  const [showAlertBanner, setShowAlertBanner] = useState(true);
  const { 
    calculateOverallProgress, 
    showProcessingProgress, 
    uploadItems, 
    setIsModalVisible,
    queueInfo,
  } = useUploadProgress();

  const PAGE_SIZE = 20;
  const { hasPermission } = useUserRole();
  const fetcher = async (page) => {
    const opts = {
      count: PAGE_SIZE
    };
    
    if (page > 1) {
      const previousPageKey = pageKeys[page - 2];
      if (previousPageKey) {
        opts.startKey = previousPageKey.StartKey;
        opts.timestampFrom = previousPageKey.timestampFrom;
      }
    }
    
    const response = await list(opts);

    if (response.StartKey && response.timestampFrom) {
      setPageKeys(prev => {
        const newKeys = [...prev];
        newKeys[page - 1] = {
          StartKey: response.StartKey,
          timestampFrom: response.timestampFrom
        };
        return newKeys;
      });
    }

    return response;
  };

  const { data, error } = useSWR(currentPage, fetcher);
  const [totalPages, setTotalPages] = useState(1);

  const checkActiveFiles = useCallback(async () => {
    try {
      const statusData = await AudioProcessingService.getSystemStatus();
      console.log("Estado del sistema:", statusData);
      
      const processing = Array.isArray(statusData.processing) 
        ? statusData.processing 
        : (statusData.processing ? [statusData.processing] : []);
      
      const queueSize = statusData.queueSize || 0;      
      if (processing.length === 0 && queueSize === 0) {
        return;
      }
      
      const activeFiles = processing.map(file => {
        const fileName = file.fileName || (file.fileId ? file.fileId.split('/').pop() : 'unknown');
        return {
          label: fileName,
          fileId: file.fileId,
          file: { name: fileName }
        };
      });
      
      const newProgress = {};
      activeFiles.forEach(item => {
        const file = processing.find(f => f.fileId === item.fileId);
        if (file) {
          newProgress[item.label] = {
            status: AudioProcessingService.mapStatusToUI(file.status || 'processing'),
            progress: AudioProcessingService.calculateProgress(file.status || 'processing'),
            error: file.error || ''
          };
        }
      });
      
      console.log(`Mostrando estado: ${activeFiles.length} archivos en proceso, ${queueSize} en cola`);
      showProcessingProgress(activeFiles, newProgress, queueSize);
      
    } catch (error) {
      console.error('Error verificando archivos activos:', error);
    }
  }, [showProcessingProgress]);

    

useEffect(() => {
  if (data) {
    if (data.StartKey && currentPage >= totalPages) {
      setTotalPages(prev => prev + 1);
    }    
  }
}, [data, currentPage, totalPages]);


  const handlePageChange = ({ detail }) => {
    const newPage = detail.currentPageIndex;
    setCurrentPage(newPage);
  };

  const { t } = useTranslation();

  const details = data ? data.Records : [];
  useDangerAlert(error, setAlert);

  useEffect(() => {
    checkActiveFiles();
  }, [checkActiveFiles]);

  return (
    <>
      <ContentLayout 
        header={
            <Header
              variant="h1"
              description={t('home.description')}
              info={<Link variant="info" ariaLabel="Info goes here.">Info</Link>}>
              {t('callList')}
            </Header>
        }>
        <Container>
          <Grid
            gridDefinition={[
              {colspan: { default:12} },
              {colspan: { default:12} },
              {colspan: { default:12} },
              {colspan: { default:12} },
              {colspan: { default:12} }
            ]}
          >
            {hasPermission("view_dashboard_metrics") && (
              <DashboardMetrics data={data} />
            )}
            {showAlertBanner && <AlertBanner />}
            {hasPermission("manage_audios") && (
              <ExpandableSection headerText={t("home.uploadCall")}>
                <Upload/>
            </ExpandableSection>
            )}
            {(uploadItems.length > 0 || (queueInfo && queueInfo.queueSize > 0)) && (
              <Container header={<Header variant="h2">{t('upload.progress', 'Progreso de archivos')}</Header>}>
                <Box padding="m">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ flexGrow: 1 }}>
                      <ProgressBar
                        value={calculateOverallProgress()}
                        label={`${calculateOverallProgress()}%`}
                        description={
                          <div>
                            {`${uploadItems.length} ${t('upload.files', 'archivos procesando')}`}
                            {queueInfo && (
                              <div>Archivos en cola: {queueInfo.queueSize}</div>
                            )}
                          </div>
                        }
                      />
                    </div>
                    <Button 
                      onClick={() => setIsModalVisible(true)}
                      iconName="external"
                      variant="link"
                      formAction="none"
                    >
                      {t('upload.viewDetails', 'Ver detalles')}
                    </Button>
                  </div>
                </Box>
              </Container>
            )}
            
            <ContactTable
              data={details}
              loading={!data && !error}
              empty={<Empty />}
              pagination={{
                currentPageIndex: currentPage,
                pagesCount: totalPages,
                onPageChange: handlePageChange
              }}
              onShowAlertBannerChange={setShowAlertBanner}
            />
            {/* <Button
            variant="normal"
            onClick={() => history.push('/analytics')}
          >
            {t("analytics.header")}
          </Button> */}
          </Grid>
        </Container>
        
      </ContentLayout>
      </>
  );
}

const Empty = () => (
  <div>
    <h2>No results</h2>
  </div>
);

export default Home;